/**
 * AboutPage — what the portal does, the acoustic analysis engine's version and what changed in each version
 * (user, 13 Oct 2026). The change log is public/engine-changes.json (a copy of the engine's docs/engine_changes.json,
 * updated with every engine release). Reachable from the home page footer and, signed in, from "ℹ️ About".
 *
 * Props: onBack (optional — shows a back button; inside the dashboard there is none)
 */
import React, { useEffect, useState } from 'react';
import './AboutPage.css';

const STEPS = [
  ['📡', 'Stations upload recordings', 'Hydrophone stations upload their audio. Each file is prepared for listening and analysis.'],
  ['🎯', 'Detection models', 'Trained models mark the 10-second pieces with calls for bottlenose dolphins, humpback whales and manatees.'],
  ['🔊', 'Acoustic analysis engine', 'Every recording, for every species, is also measured by the acoustic engine: clicks and click trains, whistles, manatee calls, humpback song units and baleen whale calls, with their codes and measurements.'],
  ['🔬', 'Review by people', 'Decoders and researchers check what the engine found: confirm, correct, dismiss, or mark calls it missed.'],
  ['📈', 'Results and learning', 'Final reviews become research results. They are also used to test every new engine version, so the engine improves where reviewers found it wrong.'],
];

export default function AboutPage({ onBack }) {
  const [log, setLog] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(1);

  useEffect(() => {
    let alive = true;
    fetch('/engine-changes.json', { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d) => { if (alive) setLog(d); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, []);

  const versions = log?.versions || [];
  return (
    <div className="about-page">
      {onBack && <button type="button" className="about-back" onClick={onBack}>← Back</button>}
      <header className="about-head">
        <h1>About Cetalingua FM analysis</h1>
        <p className="about-lead">Cetalingua FM listens to the ocean through community hydrophone stations. This portal
          turns their recordings into marine mammal detections that people can check, study and learn from.</p>
        <p className="about-version">
          Acoustic engine version <strong>{log?.current || '…'}</strong>
          {versions[0]?.date && <span className="about-date"> · updated {versions[0].date}</span>}
        </p>
      </header>

      <section className="about-section" aria-labelledby="about-how">
        <h2 id="about-how">How it works</h2>
        <ol className="about-steps">
          {STEPS.map(([icon, title, text]) => (
            <li key={title}>
              <span className="about-icon" aria-hidden="true">{icon}</span>
              <div><h3>{title}</h3><p>{text}</p></div>
            </li>
          ))}
        </ol>
        <p className="about-note">Species without a detection model yet are analysed by the acoustic engine only. Every
          result shows which engine version produced it.</p>
      </section>

      <section className="about-section" aria-labelledby="about-changes">
        <h2 id="about-changes">What changed in the engine</h2>
        {error && <p className="about-note">The change list could not be loaded right now.</p>}
        {!log && !error && <p className="about-note">Loading…</p>}
        <ul className="about-log">
          {versions.map((v, i) => (
            <li key={v.version} className={open === i + 1 ? 'is-open' : ''}>
              <button type="button" className="about-log-head" aria-expanded={open === i + 1}
                      onClick={() => setOpen(open === i + 1 ? 0 : i + 1)}>
                <span className="about-log-version">{v.version}</span>
                <span className="about-log-title">{v.title}</span>
                <span className="about-date">{v.date}</span>
              </button>
              {open === i + 1 && (
                <ul className="about-log-changes">{(v.changes || []).map((c) => <li key={c}>{c}</li>)}</ul>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
